import { useEffect, useState, useMemo } from 'react';
import { api } from '../../utils/api';
import { useTheme } from '../AdminLayout';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

/** Per-cell background opacity scales with utilization 0–100. */
function colorFor(util, accent) {
  if (util <= 0) return 'transparent';
  const alpha = clamp(0.12 + (util / 100) * 0.78, 0.12, 0.9);
  // accent expected as rgb-friendly hex like #0ea5e9; convert to rgba
  const hex = accent.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Render 7×N grid (N hours visible). Hover cell shows tooltip. */
export default function UtilizationHeatmap({ branchId, role = 'admin', days = 60 }) {
  const { T } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(null);

  // Hours to show: pick from data range for compact view (8–22 default)
  const hourRange = useMemo(() => {
    const start = 8;
    const end = 22; // exclusive
    return Array.from({ length: end - start }, (_, i) => start + i);
  }, []);

  useEffect(() => {
    if (!branchId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const fetcher = role === 'admin'
      ? api.getAdminUtilizationHeatmap
      : api.getManagerUtilizationHeatmap;
    fetcher.call(api, branchId, days)
      .then(setData)
      .catch((e) => setError(e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [branchId, role, days]);

  if (!branchId) {
    return (
      <div style={{ ...wrap(T), color: T.muted, fontSize: '0.85rem' }}>
        Select a branch to see the utilization heatmap.
      </div>
    );
  }
  if (loading) return <div style={{ ...wrap(T), color: T.muted }}>Loading heatmap…</div>;
  if (error)   return <div style={{ ...wrap(T), color: T.dangerHov }}>{error}</div>;
  if (!data)   return <div style={{ ...wrap(T), color: T.muted }}>No data</div>;

  const totals = data.grid.reduce(
    (acc, row) => row.reduce((a, c) => ({
      bookings: a.bookings + c.bookings_count,
      revenue: a.revenue + c.revenue_total,
      opportunity: a.opportunity + c.opportunity_score,
    }), acc),
    { bookings: 0, revenue: 0, opportunity: 0 },
  );

  return (
    <div style={wrap(T)}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.85rem' }}>
        <div>
          <h3 style={{
            fontFamily: "'Sora', sans-serif", fontSize: '1rem', fontWeight: 700,
            color: T.white, margin: 0,
          }}>
            Utilization Heatmap
          </h3>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: T.muted, margin: '0.15rem 0 0' }}>
            Last {data.days} days — color intensity ∝ utilization
          </p>
        </div>
        <div style={{ textAlign: 'right', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: T.muted }}>
          <div>{totals.bookings} bookings · EGP {totals.revenue.toFixed(0)}</div>
          <div style={{ color: T.orange, fontWeight: 600 }}>
            Opportunity: EGP {totals.opportunity.toFixed(0)}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `40px repeat(${hourRange.length}, minmax(0, 1fr))`,
        gap: 3,
      }}>
        <div></div>
        {hourRange.map(h => (
          <div key={`hh-${h}`} style={{
            fontSize: '0.65rem', color: T.muted, textAlign: 'center',
            fontFamily: "'DM Sans', sans-serif",
          }}>{h}</div>
        ))}
        {DAY_LABELS.map((label, dow) => [
          <div key={`dl-${dow}`} style={{
            fontSize: '0.7rem', color: T.muted, fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center',
          }}>{label}</div>,
          ...hourRange.map(h => {
            const cell = data.grid[dow]?.[h] || {
              bookings_count: 0, revenue_total: 0, utilization_pct: 0,
              no_show_rate: 0, opportunity_score: 0,
            };
            const isHover = hover && hover.dow === dow && hover.h === h;
            return (
              <div
                key={`c-${dow}-${h}`}
                onMouseEnter={() => setHover({ dow, h, cell, label })}
                onMouseLeave={() => setHover(null)}
                style={{
                  background: colorFor(cell.utilization_pct, T.orange),
                  border: `1px solid ${isHover ? T.orange : T.border}`,
                  borderRadius: 4,
                  minHeight: 26,
                  cursor: 'pointer',
                  transition: 'border-color 0.1s',
                }}
              />
            );
          }),
        ])}
      </div>

      {/* Tooltip / footer */}
      <div style={{
        marginTop: '0.85rem', minHeight: '1.6em',
        fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: T.muted,
      }}>
        {hover ? (
          <span>
            <strong style={{ color: T.text }}>{hover.label} {hover.h}:00</strong>
            {' — '}
            {hover.cell.bookings_count} bookings ·{' '}
            {hover.cell.utilization_pct}% util ·{' '}
            EGP {hover.cell.revenue_total.toFixed(0)} ·{' '}
            <span style={{ color: T.orange, fontWeight: 600 }}>
              EGP {hover.cell.opportunity_score.toFixed(0)} opportunity
            </span>
            {hover.cell.no_show_rate > 0 && (
              <span> · {hover.cell.no_show_rate}% no-shows</span>
            )}
          </span>
        ) : (
          <span>Hover any cell for details. Darker = higher utilization. Light cells with high opportunity = under-served slots.</span>
        )}
      </div>
    </div>
  );
}

function wrap(T) {
  return {
    background: T.bgCard,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  };
}
